import { SvgIcon, SvgIconProps } from '@mui/material';
import { createElement, CSSProperties, useId } from 'react';
import * as iconNames from '../../assets/SVG';

/**
 * The SVG icon component allows the user to use an SVG image as an icon.
 * Based on Figma design (https://www.figma.com/file/uTqN24vX8X6DD5zZ6XioEL/Aspen-Dental---Component-Library?node-id=9%3A597),
 * all brand icons and most functional icons in Figma are included as IconName type
 * except Tooth, Toothbrush, Tooth-open, Teeth, Ellipsis-halt and Ellipsis-v-alt icon in Figma functional icons section.
 * Use equivalent brand icons for these 6 functional icons (Tooth, Toothbrush, Tooth-open, Teeth, Ellipsis-halt, and Ellipsis-v-alt)
 * If icon is not available in the project, please add it into asset/images/SVG/Icon.tsx and use it.
 */

interface ISvgIcon extends SvgIconProps {
  /**
   * Icon props is IconName type, which include "yourself" | "virtual_care_color_alt" |... 103 more.
   * example: <TAGSvgIcon icon="yourself" />
   */
  icon: string;
  /**
   * Size is a number type, default value is 32
   */
  size?: number;
  viewBox?: string;
  /**
   * 	A [data-testid] attribute for the TAGSvgIcon used for testing purposes
   */
  dataTestId?: string;
  /**
   *  Add text to svg icon if applicable
   */
  iconText?: string;
  /**
   *  Add css to svg icon text if applicable
   */
  textStyles?: CSSProperties;
}
export type IconName = ISvgIcon['icon'];
export default function TAGSvgIcon({
  icon,
  size = 32,
  width,
  height,
  viewBox = '0 0 32 32',
  dataTestId = '',
  ...props
}: Readonly<ISvgIcon>) {
  const id = useId();
  if (!icon) return null;
  // @ts-ignore
  if (!iconNames[icon]) return null;
  const IconComponent = iconNames[icon];

  // Solve issue where we need to pass new props to icons, but are not valid HTML attributes
  const safeProps = {
    ...props
  };
  delete safeProps['textStyles'];
  delete safeProps['iconText'];

  // Use React-generated stable IDs to avoid server/client hydration mismatches.
  const titleId = `svg-title-${icon}-${dataTestId || id}`;
  return <SvgIcon {...safeProps} component="svg" sx={{
    width: width || size,
    height: height || size,
    color: props.htmlColor
  }} width={size} height={size} xmlns="http://www.w3.org/2000/svg" viewBox={viewBox} {...dataTestId && {
    'data-testid': dataTestId
  }} aria-labelledby={titleId} data-sentry-element="SvgIcon" data-sentry-component="TAGSvgIcon" data-sentry-source-file="TAGSvgIcon.tsx">
			<>
				<title id={titleId}>{icon.replaceAll('_', ' ')}</title>
				{
      // using the create element funtion because JSX does not like random attributes this makes it work
      createElement(IconComponent as React.FC<SvgIconProps>, {
        fill: props.fill ?? 'currentColor',
        stroke: props.stroke ?? 'none',
        width: width,
        height: height,
        ...props
      })}
			</>
		</SvgIcon>;
}